import { useMutation, useQueryClient } from '@tanstack/react-query'
import type { InvalidationGroup } from '@/hooks/invalidation'
import {
  optimisticAdd,
  optimisticRemove,
  optimisticSet,
  onSettledInvalidate,
  INVALIDATION_GROUPS,
} from '@/hooks/invalidation'
import { seededQuery } from '@/hooks/lists/seededQuery'
import { useDefinedQuery } from '@/hooks/useDefinedQuery'
import { withIdempotency } from '@/hooks/withIdempotency'

// Type for the add mutation input
type AddMutationInput<
  TIdKey extends string,
  TAddData = { quantity?: number; notes?: string; acquiredPrice?: number },
> = {
  [K in TIdKey]: number
} & {
  data?: TAddData
  idempotencyKey?: string
}

// Type for the update mutation input
type UpdateMutationInput<TIdKey extends string, TUpdateData> = {
  [K in TIdKey]: number
} & {
  data: TUpdateData
  idempotencyKey?: string
}

// Collection-specific config (with update operations, no share tokens)
interface CollectionHooksConfig<TIdKey extends string, TUpdateData> {
  keys: {
    quantities: () => readonly unknown[]
  }
  mutationKeys: {
    add: readonly unknown[]
    remove: readonly unknown[]
    update?: readonly unknown[]
    updateByItem?: readonly unknown[]
  }
  api: {
    addItem: (
      itemId: number,
      quantity?: number,
      acquiredPrice?: number,
      notes?: string,
      idempotencyKey?: string,
      condition?: string | null,
    ) => Promise<unknown>
    removeItem: (itemId: number, idempotencyKey?: string) => Promise<void>
    updateByItem?: (itemId: number, data: TUpdateData, idempotencyKey?: string) => Promise<unknown>
    updateItem?: (id: number, data: TUpdateData, idempotencyKey?: string) => Promise<unknown>
  }
  group: InvalidationGroup
  /** Specific keys to invalidate on settled for add/update mutations (narrowed from full group). */
  settledInvalidation?: readonly (readonly unknown[])[]
  /** Specific keys to invalidate on settled for remove mutations. */
  removeSettledInvalidation?: readonly (readonly unknown[])[]
  /**
   * On add, also optimistically drain a linked quantity-map cache by the
   * same quantity (floor at 0; delete the entry once it would hit 0).
   * Used by the card collection to keep the buylist count in sync (#101) —
   * adding cards you wanted should reduce what you still want.
   *
   * Note: only invalidate the *list* key (e.g. ['buylist']) via
   * `settledInvalidation`, not this quantities key. The quantity-map cache
   * is stub-backed (queryFn returns `{}`) and seeded by useUserQuantities;
   * invalidating it directly briefly wipes the optimistic value back to {}
   * before useUserQuantities reseeds, causing a one-frame UI flicker.
   */
  drainOnAdd?: {
    quantitiesKey: () => readonly unknown[]
  }
  idKey: TIdKey
}

type Config<TIdKey extends string, TUpdateData> = CollectionHooksConfig<TIdKey, TUpdateData>

function createUseAdd<TIdKey extends string, TUpdateData>(config: Config<TIdKey, TUpdateData>) {
  return function useAdd() {
    const queryClient = useQueryClient()
    const settled = config.settledInvalidation ?? INVALIDATION_GROUPS[config.group]
    type AddData = { quantity?: number; notes?: string; acquiredPrice?: number; condition?: string | null }
    type AddVars = AddMutationInput<TIdKey, AddData>

    const drainKey = config.drainOnAdd?.quantitiesKey()
    const quantitiesKey = config.keys.quantities()

    const mutation = useMutation({
      mutationKey: config.mutationKeys.add,
      mutationFn: (input: AddVars) => {
        const itemId = input[config.idKey as keyof typeof input] as number
        return config.api.addItem(
          itemId,
          input.data?.quantity,
          input.data?.acquiredPrice,
          input.data?.notes,
          input.idempotencyKey,
          input.data?.condition ?? undefined,
        )
      },
      ...optimisticAdd<AddVars>(
        queryClient,
        quantitiesKey,
        settled,
        (v) => v[config.idKey] as number,
        (v) => v.data?.quantity ?? 1,
        drainKey ? { drain: { quantitiesKey: drainKey } } : undefined,
      ),
    })
    return withIdempotency(mutation)
  }
}

function createUseRemove<TIdKey extends string, TUpdateData>(config: Config<TIdKey, TUpdateData>) {
  return function useRemove() {
    const queryClient = useQueryClient()
    const settled = config.removeSettledInvalidation ?? INVALIDATION_GROUPS[config.group]
    type RemoveVars = { id: number; idempotencyKey?: string }
    const mutation = useMutation({
      mutationKey: config.mutationKeys.remove,
      mutationFn: (vars: RemoveVars) => config.api.removeItem(vars.id, vars.idempotencyKey),
      ...optimisticRemove<RemoveVars>(queryClient, config.keys.quantities(), settled, (vars) => vars.id),
    })
    type PublicVars = number | RemoveVars
    const normalize = (vars: PublicVars): RemoveVars => (typeof vars === 'number' ? { id: vars } : vars)
    return withIdempotency(mutation, normalize)
  }
}

function createUseUpdate<TIdKey extends string, TUpdateData>(config: Config<TIdKey, TUpdateData>) {
  return function useUpdate() {
    if (!config.api.updateByItem || !config.mutationKeys.update) {
      throw new Error('Update hook not configured')
    }
    const queryClient = useQueryClient()
    const settled = config.settledInvalidation ?? INVALIDATION_GROUPS[config.group]
    type UpdateVars = UpdateMutationInput<TIdKey, TUpdateData>
    const mutation = useMutation({
      mutationKey: config.mutationKeys.update,
      mutationFn: (input: UpdateVars) => {
        const itemId = input[config.idKey as keyof typeof input] as number
        if (!config.api.updateByItem) throw new Error('updateByItem not configured')
        return config.api.updateByItem(itemId, input.data, input.idempotencyKey)
      },
      ...optimisticSet<UpdateVars>(
        queryClient,
        config.keys.quantities(),
        settled,
        (v) => v[config.idKey],
        (v) => (v.data as { quantity?: number })?.quantity,
      ),
    })
    return withIdempotency(mutation)
  }
}

function createUseUpdateById<TIdKey extends string, TUpdateData>(config: Config<TIdKey, TUpdateData>) {
  return function useUpdateById() {
    if (!config.api.updateItem || !config.mutationKeys.updateByItem) {
      throw new Error('UpdateById hook not configured')
    }
    const queryClient = useQueryClient()
    const settled = config.settledInvalidation ?? INVALIDATION_GROUPS[config.group]
    type UpdateByIdVars = { id: number; data: TUpdateData; idempotencyKey?: string }
    const mutation = useMutation({
      mutationKey: config.mutationKeys.updateByItem,
      mutationFn: ({ id, data, idempotencyKey }: UpdateByIdVars) => {
        if (!config.api.updateItem) throw new Error('updateItem not configured')
        return config.api.updateItem(id, data, idempotencyKey)
      },
      ...onSettledInvalidate(queryClient, settled),
    })
    return withIdempotency(mutation)
  }
}

// Factory for collection hooks (card collection/sealed collection)
export function createCollectionHooks<TIdKey extends string, TUpdateData = Record<string, unknown>>(
  config: CollectionHooksConfig<TIdKey, TUpdateData>,
) {
  // Data seeded by useUserQuantities() — never actually fetches (see seededQuery).
  const quantitiesQuery = seededQuery(config.keys.quantities)
  function useQuantities() {
    return useDefinedQuery(quantitiesQuery)
  }

  const useAdd = createUseAdd(config)
  const useRemove = createUseRemove(config)
  const useUpdate = createUseUpdate(config)
  const useUpdateById = createUseUpdateById(config)

  const result: {
    useQuantities: typeof useQuantities
    useAdd: typeof useAdd
    useRemove: typeof useRemove
    useUpdate?: typeof useUpdate
    useUpdateById?: typeof useUpdateById
  } = {
    useQuantities,
    useAdd,
    useRemove,
  }

  if (config.api.updateByItem && config.mutationKeys.update) {
    result.useUpdate = useUpdate
  }

  if (config.api.updateItem && config.mutationKeys.updateByItem) {
    result.useUpdateById = useUpdateById
  }

  return result
}
